import React from "react";
import PropTypes from "prop-types";
import { isURL } from "class-validator";
import {
  setColourShadeIfMissing,
  SIGNOL_COLOUR_NAMES,
} from "@signolteam/shared/colours";
import { SignolUtils, AppUtils } from "@src/utils";
import { COMMON_CONTROLS } from "@src/stories/utils/common-controls";
import ImageWithRetry from "../atoms/ImageWithRetry";

const CardContainer = ({
  children,
  onClick = undefined,
  thickBorderColor = undefined,
  bgColor = "white",
  shadow = true,
  border = false,
  padding = 0,
  addaptivePadding = false,
  px = null,
  py = null,
  flexibleHeight = false,
  footerImage = undefined,
  allowOverflow = false,
  zIndex = 0,
  trackingObject = {},
  className,
}) => {
  const hasFooterImage = isURL(footerImage);

  const bgClass = bgColor
    ? `bg-${setColourShadeIfMissing(bgColor, 300)}`
    : "bg-white";
  const borderClass = thickBorderColor
    ? `border-l-4 border-${thickBorderColor}-500`
    : border
      ? "border border-gray-200"
      : "";
  const onClickClass = onClick
    ? "cursor-pointer hover:shadow-lg transition-shadow duration-200"
    : "";
  const shadowClass = shadow ? "shadow-md" : ""; // `shadow-md shadow-${thickBorderColor}-500/50` : "";
  const heightClass = flexibleHeight ? "" : "h-full";

  let paddingClass =
    padding > 0
      ? `p-${padding}`
      : px && py
        ? `px-${px} py-${py}`
        : px
          ? `px-${px}`
          : py
            ? `py-${py}`
            : "";
  paddingClass = addaptivePadding
    ? "py-4 px-4 sm:py-6 sm:px-6 lg:py-8 lg:px-10"
    : paddingClass;

  return (
    <div
      data-id="CardContainer"
      className={`
        rounded-2xl
        ${!!allowOverflow ? "relative" : "overflow-hidden"}
        ${bgClass}
        ${borderClass}
        ${shadowClass}
        ${heightClass}
        ${onClick ? onClickClass : ""}
        ${hasFooterImage ? "flex flex-col" : paddingClass}
        ${className}
      `}
      {...(onClick ? { onClick } : {})}
      {...SignolUtils.makeTrackableDataAttributes(trackingObject)}
      style={{ zIndex }}
    >
      {hasFooterImage && (
        <>
          <div className={`flex-1 p-${padding}`}>{children}</div>
          <ImageWithRetry
            src={footerImage}
            alt=""
            className="w-full mt-4 object-contain overflow-hidden relative -bottom-px"
          />
        </>
      )}
      {!hasFooterImage && children}
    </div>
  );
};

export const cardContainerControls = {
  children: {
    propType: PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.element,
      PropTypes.string,
    ]),
    description: "Card content",
    disable: true,
  },
  onClick: {
    propType: PropTypes.func,
    description: "Card clicking action",
    controlType: "function",
    defaultValue: () => alert("clicking card"),
  },
  thickBorderColor: {
    propType: PropTypes.string,
    description: "Colour for the thick left border",
    controlType: "select",
    options: [undefined, ...SIGNOL_COLOUR_NAMES],
  },
  shadow: {
    propType: PropTypes.bool,
    description: "Enable card shadow",
    controlType: "boolean",
    defaultValue: true,
  },
  border: {
    propType: PropTypes.bool,
    description: "Enable borders",
    controlType: "boolean",
    defaultValue: true,
  },
  bgColor: COMMON_CONTROLS.colour_name,
  padding: {
    propTypes: PropTypes.number,
    controlType: "select",
    options: [0, 1, 2, 3, 4, 5, 6],
    description: "Padding inside the card the card",
  },
  flexibleHeight: {
    propType: PropTypes.bool,
    description: "Enable card shadow",
    controlType: "boolean",
    defaultValue: false,
  },
  footerImage: {
    propTypes: PropTypes.string,
    controlType: "text",
    description: "Footer image",
  },
  allowOverflow: {
    propTypes: PropTypes.bool,
    controlType: "boolean",
    description: "Allow eg. tooltips to be visible when outside its container",
  },
  zIndex: {
    propTypes: PropTypes.number,
    controlType: "select",
    options: [0, 10, 20, 30, 40, 50, 60],
    description:
      "Z-index ie. stacking/overlap order. Used with allowOverflow to define how overflowing elements overlap",
  },
};

CardContainer.propTypes = AppUtils.getPropTypeFromControls(
  cardContainerControls,
);

export default CardContainer;
